<template>
  <v-dialog v-model="dialog" scrollable :max-width="exporting ? '650px' : '90%'">
    <template v-slot:activator="{ on }">
      <span v-if="!historyType" v-on="on">
        <mf-button text :label="$t('campanhas.ver_historico')" />
      </span>
      <span v-else class="text-link" v-on="on">{{ historyType }}</span>
    </template>
    <v-card class="historico-campanha">
      <v-card-title>
        <span class="table-card-title pa-2 primary-text">{{ $t('campanhas.historico') }} {{ historyType }}</span>
        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <mf-icon small color="cinza-0">info</mf-icon>
            </span>
          </template>
          <span>{{ $t('campanhas.historico_tooltip') }}</span>
        </v-tooltip>
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <paginated-data-table
          v-if="!exporting"
          ref="table"
          :headers="headers"
          :query="query"
          :graphql-uri="$microservicesUrls['campaigns']"
          query-name="messagesHistory"
          :query-main-property-name="'messages_history'"
          :query-variables="queryVariables"
          :fixed-max-records="totalSendings"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ formatDateTime(item.date).date }}</td>
              <td>{{ formatDateTime(item.date).time }}</td>
              <td>
                <a @click="redirect(item.client_id)">{{ item.client_id }}</a>
              </td>
              <td>{{ item.contact | telefone }}</td>
              <td>{{ item.content }}</td>
              <td>{{ formatStatus(item.status) }}</td>
              <td v-if="showCost">
                {{ industry ? 0 : item.price }}
              </td>
            </tr>
          </template>
        </paginated-data-table>
        <include-emails-table v-else ref="includeEmailsTable" :emails.sync="emails" :max-emails-to-scroll="7" scroll-height="300px" />
        <v-card-actions class="d-flex justify-end mr-2">
          <mf-button class="mr-3" text label="Cancelar" @click="exporting ? (exporting = false) : (dialog = false)" />
          <mf-button v-if="!exporting" label="Exportar" :loading="loading" @click="exporting = true" />
          <export-data v-else async :loading="loading" @file-format="getFileFormat"></export-data>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'
import ExportData from '../../../../../components/export-data/ExportData.vue'
import { SEND_HISTORY_REPORT } from '@/modules/campanhas/views/components/graphql'

import IncludeEmailsTable from '@/components/export-email-report/IncludeEmailsTable.vue'
export default {
  components: {
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
    IncludeEmailsTable,
    ExportData,
  },
  props: {
    historyType: {
      type: String,
      default: () => '',
    },
    query: {
      type: Object,
      required: true,
    },
    campaignId: String,
    campaignType: String,
    totalSendings: Number,
    showCost: {
      type: Boolean,
      default: true,
    },
    industry: null,
  },
  data: () => ({
    dialog: false,
    exporting: false,
    loading: false,
    DateTime: DateTime,
    emails: [],
    fileFormat: 'CSV',
  }),
  computed: {
    queryVariables() {
      return {
        id: this.campaignId,
        ...(this.historyType ? { channels: this.getChannels } : {}),
      }
    },
    getChannels() {
      if (this.campaignType) return [this.campaignType]
      return [this.historyType.toLowerCase() === 'push aplicativo' ? 'push' : this.historyType.toLowerCase()]
    },
    headers() {
      const items = [
        {
          text: this.$t('campanhas.data'),
          value: '`messages_history.{id}.date`',
          width: '9%',
        },
        {
          text: this.$t('campanhas.hora'),
          value: '`messages_history.{id}.date`',
          width: '9%',
          sortable: false,
        },
        {
          text: this.$t('campanhas.cliente'),
          value: '`messages_history.{id}.client_id`',
          width: '10%',
        },
        {
          text: this.$t('campanhas.numero'),
          value: '`messages_history.{id}.contact`',
          width: '12%',
        },
        {
          text: this.$t('campanhas.mensagem'),
          value: '`messages_history.{id}.content`',
          width: '40%',
        },
        {
          text: this.$t('campanhas.status'),
          value: '`messages_history.{id}.status`',
          width: '12%',
        },
      ]
      if (this.showCost) {
        items.push({
          text: this.$t('campanhas.custo'),
          value: '`messages_history.{id}.price`',
          width: '7%',
        })
      }
      return items
    },
  },
  methods: {
    redirect(param) {
      this.$router.push({ path: `/clientes/detalhes-cliente/${param}` }).catch(() => {})
    },
    formatStatus(status) {
      switch (status) {
        case 0:
          return 'Não-entregue'
        case 1:
          return 'Pendente'
        case 2:
          return 'Recebido'
        case 3:
          return 'Lido'
        case 4:
          return 'Clicado'
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime) return { date: '-', time: '-' }

      const [date, time] = dateTime.split(' ')
      return {
        date: DateTime.fromISO(date).toFormat('dd/MM/yyyy'),
        time: DateTime.fromISO(time).toFormat('HH:mm'),
      }
    },
    getFileFormat(value) {
      this.fileFormat = value
      this.exportarCsv()
    },
    async exportarCsv() {
      const isValid = this.$refs.includeEmailsTable.isValid()
      if (!isValid) return
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: SEND_HISTORY_REPORT,
          variables: {
            emails: this.emails,
            channels: this.getChannels,
            campaignId: this.campaignId,
            fileFormat: this.fileFormat.toUpperCase(),
          },
          context: {
            uri: this.$microservicesUrls['asyncJobs'],
          },
        })
        .then(() => {
          this.exporting = false
          this.loading = false
          this.$snackbar({ message: 'E-mail enviado com sucesso', snackbarColor: 'success' })
        })
        .catch(() => {
          this.loading = false
          this.$snackbar({ message: 'Erro ao exportar relatório', snackbarColor: 'error' })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.text-link {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  display: flex;
  align-items: center;
  letter-spacing: 0.01em;
  text-decoration-line: underline;
  cursor: pointer;
  color: $marca-0-l40 !important;
}
</style>
